/**
 * NegotiationsInFlightView · Chunk · operator's live dashboard of open kits.
 *
 * Pulls `/api/should-cost/in-flight?days=30`. Each row is one
 * `negotiation_kit_sends` Atlas record joined with current decision_queue
 * state. Status tags rendered as small-caps pills:
 *   FRESH        · sent <3 days ago
 *   IN_DIALOGUE  · sent 3–13 days ago
 *   NUDGE_DUE    · sent ≥14 days ago (operator should follow up)
 *   STALE        · underlying driver data refreshed since send · recompute
 *                  recommended before any further negotiation move
 */
import React, { useEffect, useState, useMemo } from "react";
import { api, fmtUSD, fmtPct } from "../lib/api";
import DecisionModal from "./DecisionModal";

const TIFFANY = "#0D9488";
const TEAL_DEEP = "#0D6964";
const SLATE_INK = "#0F172A";
const CREAM = "#FAF5EB";

const TAG_PILL = {
  FRESH:       { label: "fresh",       bg: "#ECFDF5", fg: "#065F46" },
  IN_DIALOGUE: { label: "in dialogue", bg: `${TIFFANY}22`, fg: TEAL_DEEP },
  NUDGE_DUE:   { label: "nudge due",   bg: "#FEF3C7", fg: "#92400E" },
  STALE:       { label: "stale",       bg: "#FEE2E2", fg: "#991B1B" },
};

const ACTION_PILL = {
  switch:      { label: "switch",      bg: "#B45309",    fg: "#FFFFFF" },
  negotiate:   { label: "negotiate",   bg: TIFFANY,      fg: "#FFFFFF" },
  consolidate: { label: "consolidate", bg: SLATE_INK,    fg: "#F9FAFB" },
  defer:       { label: "defer",       bg: "#F1F5F9",    fg: "#334155" },
  no_action:   { label: "no action",   bg: "#E5E7EB",    fg: "#374151" },
};

export default function NegotiationsInFlightView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [tagFilter, setTagFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.inFlight(windowDays).then(setRows).finally(() => setLoading(false));
  }, [windowDays]);

  const filtered = useMemo(
    () => (tagFilter === "ALL" ? rows : rows.filter((r) => r.status_tag === tagFilter)),
    [rows, tagFilter],
  );

  const counts = useMemo(() => {
    const c = { FRESH: 0, IN_DIALOGUE: 0, NUDGE_DUE: 0, STALE: 0 };
    for (const r of rows) c[r.status_tag] = (c[r.status_tag] || 0) + 1;
    return c;
  }, [rows]);

  const totalInFlight = rows.reduce(
    (s, r) => s + (r.gap_total_usd && r.gap_total_usd > 0 ? r.gap_total_usd : 0), 0,
  );

  return (
    <div data-testid="negotiations-in-flight-view" style={{ color: SLATE_INK }}>
      {/* KPI strip */}
      <div className="mb-5 grid md:grid-cols-4 gap-3">
        <Kpi label="In flight" value={String(rows.length)} sub={`window · ${windowDays} days`} />
        <Kpi label="Nudge due" value={String(counts.NUDGE_DUE)} tone={counts.NUDGE_DUE > 0 ? "amber" : null}
             sub={counts.NUDGE_DUE > 0 ? "≥ 14 days · no recorded action" : "all kits within follow-up window"} />
        <Kpi label="Stale (recompute)" value={String(counts.STALE)} tone={counts.STALE > 0 ? "red" : null}
             sub="driver data refreshed since send" />
        <Kpi label="Open addressable" value={fmtUSD(totalInFlight)} tone="tiffany"
             sub="sum of positive gaps currently in-flight" />
      </div>

      {/* Window + filter pills */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#64748B" }}>Window</div>
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setWindowDays(d)}
            data-testid={`window-${d}`}
            className="text-xs px-3 py-1 rounded-full transition"
            style={{ background: windowDays === d ? TIFFANY : "#F1F5F9",
                     color: windowDays === d ? "#FFFFFF" : SLATE_INK }}>
            {d}d
          </button>
        ))}
        <div className="ml-4 text-[10px] tracking-[0.18em] uppercase" style={{ color: "#64748B" }}>Tag</div>
        {["ALL", "FRESH", "IN_DIALOGUE", "NUDGE_DUE", "STALE"].map((t) => (
          <button key={t} onClick={() => setTagFilter(t)}
            data-testid={`tag-filter-${t}`}
            className="text-xs px-3 py-1 rounded-full transition"
            style={{ background: tagFilter === t ? TIFFANY : "#F1F5F9",
                     color: tagFilter === t ? "#FFFFFF" : SLATE_INK }}>
            {t === "ALL" ? "All" : TAG_PILL[t]?.label}
          </button>
        ))}
      </div>

      {/* Card list */}
      <div className="space-y-2">
        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: "#64748B" }}>Loading in-flight kits…</div>
        ) : filtered.length === 0 ? (
          <EmptyState totalInWindow={rows.length} tagFilter={tagFilter} />
        ) : (
          filtered.map((r) => (
            <FlightCard key={r.send_id} r={r}
              onOpen={() => { setSelected(r.decision_id); setModalOpen(true); }} />
          ))
        )}
      </div>

      <DecisionModal open={modalOpen} onOpenChange={setModalOpen} decisionId={selected} />
    </div>
  );
}

function FlightCard({ r, onOpen }) {
  const tag = TAG_PILL[r.status_tag] || TAG_PILL.FRESH;
  const action = ACTION_PILL[r.recommended_action];
  const gapColor =
    r.gap_pct == null ? "#64748B" :
    Math.abs(r.gap_pct) > 15 ? "#9F1239" :
    Math.abs(r.gap_pct) > 5 ? "#B45309" : SLATE_INK;
  return (
    <div data-testid={`flight-${r.send_id}`}
      onClick={onOpen}
      className="rounded-md p-4 transition cursor-pointer hover:shadow-sm"
      style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-3">
          <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#94A3B8" }}>
            Sent {r.days_elapsed === 0 ? "today" : `${r.days_elapsed} day${r.days_elapsed === 1 ? "" : "s"} ago`}
          </div>
          <div className="text-sm font-medium mt-1" style={{ color: SLATE_INK,
               fontFamily: '"EB Garamond", Garamond, serif' }}>
            {r.supplier_company || "Supplier"}
          </div>
          <div className="text-[11px] italic mt-0.5" style={{ color: "#64748B" }}>
            to {r.recipient}{r.cc ? ` · cc ${r.cc}` : ""}
          </div>
        </div>
        <div className="col-span-3">
          <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#94A3B8" }}>Template · Line</div>
          <div className="text-xs mt-1" style={{ color: SLATE_INK }}>
            <code className="text-[11px]">{r.template_id || "no match"}</code>
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>
            line <code>{r.po_line_id || "—"}</code>
          </div>
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#94A3B8" }}>Gap</div>
          <div className="text-base font-medium mt-1 tabular-nums" style={{ color: gapColor }}>
            {r.gap_pct == null ? "—" : `${r.gap_pct > 0 ? "+" : ""}${r.gap_pct.toFixed(1)}%`}
          </div>
          <div className="text-[11px] tabular-nums" style={{ color: gapColor }}>
            {r.gap_total_usd == null ? "—" :
             `${r.gap_total_usd >= 0 ? "+" : "−"}${fmtUSD(Math.abs(r.gap_total_usd))}`}
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1 items-start">
          <Pill spec={tag} />
          {action && <Pill spec={action} />}
        </div>
        <div className="col-span-2 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            data-testid={`flight-open-${r.send_id}`}
            className="text-xs px-3 py-1.5 rounded font-medium transition"
            style={{ background: `${TIFFANY}22`, color: TEAL_DEEP, border: `1px solid ${TIFFANY}` }}>
            Open decision →
          </button>
        </div>
      </div>
      {r.status_tag === "NUDGE_DUE" && (
        <div className="mt-3 pt-3 border-t text-xs italic" style={{ borderColor: "#FEF3C7", color: "#92400E" }}>
          14+ days without a recorded action. Consider resending the kit with a follow-up note, or marking the action via the modal.
        </div>
      )}
      {r.status_tag === "STALE" && (
        <div className="mt-3 pt-3 border-t text-xs italic" style={{ borderColor: "#FEE2E2", color: "#991B1B" }}>
          Underlying driver data refreshed since this kit was sent. Recompute the decision before any further move — the supportable price may have changed.
        </div>
      )}
    </div>
  );
}

function EmptyState({ totalInWindow, tagFilter }) {
  if (totalInWindow === 0) {
    return (
      <div className="py-12 text-center" style={{ background: CREAM,
        border: "1px dashed #C9BFA8", borderRadius: 6,
        fontFamily: '"EB Garamond", Garamond, serif' }}>
        <div className="text-base italic" style={{ color: SLATE_INK }}>
          No negotiation kits in flight.
        </div>
        <div className="text-xs mt-2 italic" style={{ color: "#64748B" }}>
          Open any build-up decision in the Decision Queue, click <em>Email kit to category manager</em>,
          and the kit will appear here once sent.
        </div>
      </div>
    );
  }
  return (
    <div className="py-10 text-center text-xs italic" style={{ color: "#64748B" }}>
      No kits match the <em>{tagFilter}</em> filter in this window.
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  const color = tone === "tiffany" ? TIFFANY :
                tone === "amber" ? "#B45309" :
                tone === "red" ? "#9F1239" : SLATE_INK;
  return (
    <div className="p-3 rounded-md" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
      <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#64748B" }}>{label}</div>
      <div className="text-2xl mt-1 font-medium tabular-nums" style={{ color,
           fontFamily: '"EB Garamond", "Cormorant Garamond", Garamond, serif' }}>{value}</div>
      {sub && <div className="text-[10px] mt-1 italic" style={{ color: "#64748B" }}>{sub}</div>}
    </div>
  );
}

function Pill({ spec }) {
  if (!spec) return null;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider"
      style={{ background: spec.bg, color: spec.fg }}>
      {spec.label}
    </span>
  );
}
